import { createElement, type ReactNode } from "react";
import "./RichText.css";
import type { JSONContent } from "@tiptap/core";
const safeLink = (href: unknown) =>
  typeof href === "string" &&
  /^(https?:\/\/|mailto:|tel:|\/(?!\/)|#)/i.test(href)
    ? href
    : undefined;
function render(node: JSONContent, key: number): ReactNode {
  if (node.type === "text") {
    let text: ReactNode = node.text;
    for (const mark of node.marks ?? []) {
      const tag = (
        {
          bold: "strong",
          italic: "em",
          strike: "s",
          code: "code",
          underline: "u",
        } as Record<string, string>
      )[mark.type];
      if (tag) text = createElement(tag, { key }, text);
      if (mark.type === "link")
        text = (
          <a
            key={key}
            href={safeLink(mark.attrs?.href)}
            rel="noopener noreferrer"
          >
            {text}
          </a>
        );
    }
    return <span key={key}>{text}</span>;
  }
  const tag =
    (
      {
        doc: "div",
        paragraph: "p",
        heading: `h${Math.max(1, Math.min(3, Number(node.attrs?.level) || 2))}`,
        blockquote: "blockquote",
        bulletList: "ul",
        orderedList: "ol",
        listItem: "li",
        horizontalRule: "hr",
        hardBreak: "br",
      } as Record<string, string>
    )[node.type ?? ""] ?? "div";
  return createElement(
    tag,
    {
      key,
      ...(tag === "ol" ? { start: node.attrs?.start } : {}),
      ...(tag === "blockquote" && node.attrs?.quoteStyle
        ? { "data-quote-style": node.attrs.quoteStyle }
        : {}),
      ...(node.attrs?.tone ? { "data-tone": node.attrs.tone } : {}),
      ...(node.attrs?.align ? { "data-align": node.attrs.align } : {}),
      ...(node.attrs?.size ? { "data-size": node.attrs.size } : {}),
    },
    ["br", "hr"].includes(tag) ? undefined : node.content?.map(render),
  );
}
/**
 * Renders stored rich text. `flat` returns its elements without wrappers, so
 * they sit directly in a page's block flow, as they do in the editor.
 */
export function RichText({ value, flat }: { value: unknown; flat?: boolean }) {
  if (flat) {
    if (typeof value === "string")
      return <p className="whitespace-pre-wrap">{value}</p>;
    if (!value || typeof value !== "object") return null;
    const node = value as JSONContent;
    return node.type === "doc" ? <>{node.content?.map(render)}</> : render(node, 0);
  }
  return (
    <div className="document-rich-text">
      {typeof value === "string" ? (
        <p className="whitespace-pre-wrap">{value}</p>
      ) : value && typeof value === "object" ? (
        render(value as JSONContent, 0)
      ) : null}
    </div>
  );
}
